import { Link } from "react-router-dom";
import { duplicateUploadMessage } from "../utils/expense-duplicate-message";

/**
 * One refused upload as the expense upload end screens show it (XPEN-3618).
 *
 * A duplicate refusal uses the duplicate message and, when the server named one by a
 * valid id, the other expense account to link to (see utils/expense-duplicate-message).
 * Any other refusal shows the reason the server sent, or a translated "could not be
 * added" when it sent none, so the screen never shows "undefined" or a bare name.
 *
 * @param {object} data - The app store; data.t reads translations.
 * @param {string} name - The file name.
 * @param {*} errorBody - The refusal's response body (response.response.data), if any.
 * @param {string} currentAccountId - The expense account the receipt was uploaded to.
 * @returns {{name: string, text: string, reportId: (string|null)}} The row to show.
 */
export const uploadRefusalRow = (data, name, errorBody, currentAccountId) => {
  const duplicate = duplicateUploadMessage(data, errorBody, currentAccountId);
  if (duplicate) return { name, text: duplicate.text, reportId: duplicate.reportId };

  const reason = typeof errorBody?.message === "string" ? errorBody.message.trim() : "";
  return { name, text: reason || data.t.read("expense_upload.error_not_added"), reportId: null };
};

/**
 * The refused-receipts box of the expense upload end screens (XPEN-3618): a title that
 * counts them, then one bulleted item per receipt with its name in bold, its reason, and
 * a link to the other expense account when there is one.
 *
 * It must be a direct child of .uploading-container, so it stretches to the card's width.
 *
 * @param {object} props
 * @param {object} props.data - The app store; data.t reads translations.
 * @param {Array<{name: string, text: string, reportId: (string|null)}>} props.rows - The
 *   refused receipts, as built by uploadRefusalRow.
 * @returns {React.ReactNode} The box.
 */
const UploadRefusalList = ({ data, rows }) => {
  const title = rows.length === 1
    ? data.t.read("expense_upload.error_not_added_count_one")
    : data.t.read("expense_upload.error_not_added_count_other")?.replace("{n}", String(rows.length));

  return (
    <div className="alert alert-warning upload-refusals" role="alert">
      <div className="font-weight-bold mb-2">{title}</div>
      <ul className="mb-0 pl-3">
        {rows.map((row, i) => (
          <li key={i} data-testid="error-message">
            {/* The spaces show nothing (the parts are blocks) but keep the text readable as
                "name reason link" for screen readers and tests. */}
            <strong className="d-block">{row.name}</strong>
            {" "}
            <span className="d-block">{row.text}</span>
            {row.reportId && (
              <>
                {" "}
                <Link to={`/expenses_account/${row.reportId}`} className="alert-link d-block">
                  {data.t.read("expense_duplicate.open_report")}
                </Link>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default UploadRefusalList;
